/* ===========================================================================
 * DAY-KEY ARITHMETIC — calendar days as `YYYY-MM-DD` strings, and nothing else.
 *
 * docs/time-and-dates.md is binding, and its first line is the reason this file
 * is a pile of string functions rather than a thin wrapper over `Date`: the
 * company timezone is the system clock, the browser's zone is a viewing
 * preference. A picker that built a `new Date()` to decide what "today" is —
 * or that handed a `Date` back as its value — would be asking the reader's
 * MACHINE a question only the tenant's configured zone may answer, and would be
 * wrong by exactly one day for the first four hours of every Gulf day.
 *
 * So:
 *   • Nothing here reads a clock. There is no `now()`, no `today()`, no
 *     default argument that falls back to one. "Today" arrives as a day key
 *     from the caller (`useCompanyToday()` in apps/web) or it does not arrive
 *     at all, and the grid simply draws no ring.
 *   • The value type is `string`. A `Date` in a prop is an instant, and an
 *     instant truncated to a day is the incident that document records.
 *
 * WHY `Date.UTC` IS FINE HERE. §4 of docs/time-and-dates.md names this exact
 * exception: a grid construction where both ends are exact UTC midnights is a
 * bare calendar day carried in a Date box, with no zone left in it to shift.
 * Every construction below is `Date.UTC(y, m - 1, d)` and every read-back is
 * `getUTC*`, so the local zone is never consulted in either direction. The
 * seed — what day it is — always comes from the caller.
 * ======================================================================== */

/** A calendar day as `YYYY-MM-DD`. The only date type this package accepts. */
export type DayKey = string;

/** A day key taken apart. `month` is 1–12, as a human writes it. */
export interface Ymd {
  year: number;
  month: number;
  day: number;
}

const KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** `2026`, `9`, `1` → `'2026-09-01'`. No validation: callers build from parts. */
export function toDayKey(year: number, month: number, day: number): DayKey {
  return `${String(year).padStart(4, '0')}-${pad2(month)}-${pad2(day)}`;
}

/**
 * The parts of a day key, or `null` if it is not one — including the shapes
 * that LOOK like one and are not: `2026-13-01`, `2026-02-30`. A picker that
 * accepted 30 February would draw a cell nobody could ever match.
 */
export function parseDayKey(key: string | null | undefined): Ymd | null {
  if (!key) return null;
  const m = KEY_RE.exec(key);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

/** Whether `v` is a real calendar day in `YYYY-MM-DD` form. */
export function isDayKey(v: string | null | undefined): boolean {
  return parseDayKey(v) !== null;
}

/**
 * How many days `month` (1–12) has in `year`. Day 0 of the NEXT month is the
 * last day of this one, which is the only leap-year rule anybody should write.
 */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** `n` days after `key` (negative steps back). Counted in DAYS, never in ms. */
export function addDays(key: DayKey, n: number): DayKey {
  const p = parseDayKey(key);
  if (!p) return key;
  const d = new Date(Date.UTC(p.year, p.month - 1, p.day + n));
  return toDayKey(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

/**
 * `n` months after `key`, with the day CLAMPED to the target month's length —
 * 31 January plus one month is 28 February, not 3 March. PageDown on the 31st
 * is exactly this case, and rolling over would move the reader two months.
 */
export function addMonths(key: DayKey, n: number): DayKey {
  const p = parseDayKey(key);
  if (!p) return key;
  const total = p.year * 12 + (p.month - 1) + n;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  return toDayKey(year, month, Math.min(p.day, daysInMonth(year, month)));
}

/**
 * The weekday, MONDAY FIRST: 0 = Monday … 6 = Sunday.
 *
 * Monday-first is not a preference here, it is agreement. The attendance
 * calendar (`myAttendanceMonths.ts`) puts Monday in the first column with the
 * same `(day + 6) % 7`, and a picker that disagreed with the grid beside it
 * would put the same date under two different weekday headings on one screen —
 * design-rules §3.2 in its purest form.
 */
export function weekdayMondayFirst(key: DayKey): number {
  const p = parseDayKey(key);
  if (!p) return 0;
  return (new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay() + 6) % 7;
}

/** The first day of `key`'s month. */
export function startOfMonth(key: DayKey): DayKey {
  const p = parseDayKey(key);
  return p ? toDayKey(p.year, p.month, 1) : key;
}

/** The Monday of `key`'s week, and the Sunday that ends it. */
export function startOfWeek(key: DayKey): DayKey {
  return addDays(key, -weekdayMondayFirst(key));
}
export function endOfWeek(key: DayKey): DayKey {
  return addDays(key, 6 - weekdayMondayFirst(key));
}

/**
 * Day keys compare correctly as STRINGS — that is the whole reason the format
 * is fixed-width and big-endian — so this exists to say so at the call site
 * rather than to do anything clever.
 */
export function compareDayKeys(a: DayKey, b: DayKey): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Whether `key` sits inside `[min, max]`; an absent bound does not bind. */
export function withinBounds(
  key: DayKey,
  min?: DayKey | null,
  max?: DayKey | null
): boolean {
  if (min && isDayKey(min) && key < min) return false;
  if (max && isDayKey(max) && key > max) return false;
  return true;
}

/** `key` pulled inside `[min, max]`. Used to land the caret on a legal day. */
export function clampDayKey(
  key: DayKey,
  min?: DayKey | null,
  max?: DayKey | null
): DayKey {
  let out = key;
  if (min && isDayKey(min) && out < min) out = min;
  if (max && isDayKey(max) && out > max) out = max;
  return out;
}

/** One cell of a month grid. `outside` days belong to the neighbouring month. */
export interface MonthCell {
  key: DayKey;
  day: number;
  outside: boolean;
}

/**
 * The six-week, Monday-first grid for `year`/`month`, leading and trailing days
 * included rather than blanked.
 *
 * SIX WEEKS ALWAYS — 42 cells, even when five would hold the month. A grid that
 * changes height between months makes the footer jump under the reader's
 * cursor as they step through the year, and in a popover that is anchored to a
 * trigger it also re-runs the placement and can flip the panel above the
 * control mid-click.
 *
 * REAL DAYS, NOT BLANKS, in the leading and trailing slots: they are drawn in
 * `text-foreground-disabled` but they are still selectable, because a reader
 * looking at September who wants 1 October can see it and should be able to
 * take it. `myAttendanceMonths` pushes `null` there for the opposite reason —
 * it is rendering a fixed range, not offering a choice.
 */
export function monthGrid(year: number, month: number): MonthCell[] {
  const first = toDayKey(year, month, 1);
  const lead = weekdayMondayFirst(first);
  const start = addDays(first, -lead);
  const cells: MonthCell[] = [];
  for (let i = 0; i < 42; i += 1) {
    const key = addDays(start, i);
    const p = parseDayKey(key);
    /* `p` cannot be null — `start` is a real day and `addDays` of a real day is
       a real day — but the type says it can, and a non-null assertion in a
       loop is how a future edit gets to throw at render. */
    if (!p) continue;
    cells.push({ key, day: p.day, outside: p.year !== year || p.month !== month });
  }
  return cells;
}
